import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useParagonStore } from '@/stores/useParagonStore'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Search, Eye, EyeOff, LogIn, LogOut, Building2 } from 'lucide-react'

/* ── Types ── */
interface TenantCard {
  tenant_id: string
  tenant_name: string
  slug: string
  plan: string
  status?: string
  owner_email?: string
  user_count: number
  health_status?: string
  last_login: string | null
}

interface AuditEntry {
  id: string
  timestamp: string
  action: 'SESSION_START' | 'SESSION_END' | 'BLOCKED'
  tenant: string
  user_email: string
  reason?: string
  duration?: string
}

/* ── Mock audit data (until audit table exists) ── */
const MOCK_AUDIT: AuditEntry[] = [
  {
    id: '1',
    timestamp: '2026-02-15 17:02',
    action: 'SESSION_END',
    tenant: 'Glamour Studio',
    user_email: 'admin@paragon.dev',
    reason: 'Routine health check',
    duration: '22m 14s',
  },
  {
    id: '2',
    timestamp: '2026-02-15 16:40',
    action: 'SESSION_START',
    tenant: 'Glamour Studio',
    user_email: 'admin@paragon.dev',
    reason: 'Customer support request',
  },
  {
    id: '3',
    timestamp: '2026-02-15 15:15',
    action: 'BLOCKED',
    tenant: 'Nova Studio',
    user_email: 'admin@paragon.dev',
    reason: 'Financial data access denied',
  },
  {
    id: '4',
    timestamp: '2026-02-15 14:22',
    action: 'SESSION_END',
    tenant: 'Nova Studio',
    user_email: 'admin@paragon.dev',
    reason: 'JWT expired — auto-terminated',
    duration: '38m 01s',
  },
]

const PLAN_BORDERS: Record<string, string> = {
  Paragon: 'border-[rgba(201,168,76,0.2)] text-[#C9A84C]',
  Pro: 'border-[rgba(99,102,241,0.2)] text-[rgba(99,102,241,0.7)]',
  Starter: 'border-white/[0.08] text-white/30',
}

function PlanBadge({ plan }: { plan: string }) {
  const cls = PLAN_BORDERS[plan] ?? PLAN_BORDERS.Starter
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls} font-mono`}>
      {plan || 'Starter'}
    </span>
  )
}

function ConfirmModal({
  target,
  reason,
  onReasonChange,
  onClose,
  onConfirm,
}: {
  target: TenantCard
  reason: string
  onReasonChange: (value: string) => void
  onClose: () => void
  onConfirm: () => void
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/70"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      role="presentation"
    >
      <div
        className="w-[420px] rounded-xl border border-[rgba(248,113,113,0.15)] bg-white dark:bg-[#171717] shadow-xl p-6"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[rgba(251,191,36,0.08)] border border-[rgba(251,191,36,0.12)] flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-[rgba(248,113,113,0.7)]" />
          </div>
          <div>
            <div className="text-[14px] text-zinc-900 dark:text-zinc-50 font-medium">
              Begin Impersonation Session
            </div>
            <div className="text-[12px] text-zinc-500 dark:text-white/25">
              This action is fully audited
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-zinc-50 dark:bg-white/[0.03] border border-black/[0.07] dark:border-white/[0.06] p-3 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-zinc-400 dark:text-white/25" />
            <span className="text-[13px] text-zinc-900 dark:text-zinc-50">
              {target.tenant_name}
            </span>
            <PlanBadge plan={target.plan} />
          </div>
          <div className="text-[11px] font-mono text-zinc-400 dark:text-white/25">
            {target.slug}.phyre.tech
          </div>
        </div>

        <div className="mb-4">
          <label
            htmlFor="impersonation-reason"
            className="block text-[11px] text-zinc-500 dark:text-white/25 mb-1.5 tracking-wider"
          >
            REASON FOR ACCESS
          </label>
          <textarea
            id="impersonation-reason"
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="e.g. Customer support request — ticket #1234"
            rows={2}
            className="w-full px-3 py-2.5 rounded-lg bg-zinc-50 dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.06] text-[13px] text-zinc-900 dark:text-zinc-50 placeholder-zinc-400/60 dark:placeholder-white/15 focus:border-[rgba(248,113,113,0.3)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(248,113,113,0.3)] resize-none transition-colors duration-150"
          />
          <div className="text-[10px] text-zinc-400 dark:text-white/15 mt-1">
            {reason.length} chars — min 6 required
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg text-[13px] text-zinc-500 dark:text-white/30 border border-black/[0.07] dark:border-white/[0.06] hover:bg-zinc-50 dark:hover:bg-white/[0.03] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35"
          >
            Cancel
          </button>
          <button
            disabled={reason.length <= 5}
            onClick={onConfirm}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-[13px] border transition-colors disabled:opacity-30 disabled:cursor-not-allowed bg-[rgba(248,113,113,0.12)] border-[rgba(248,113,113,0.2)] text-[rgba(248,113,113,0.8)] hover:bg-[rgba(248,113,113,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35"
          >
            <LogIn className="w-3.5 h-3.5" />
            Begin Session
          </button>
        </div>
      </div>
    </div>
  )
}

function AuditTab() {
  return (
    <>
      <div className="mb-4 rounded-xl border border-[rgba(251,191,36,0.12)] bg-[rgba(251,191,36,0.04)] px-4 py-3">
        <div className="text-[11px] font-mono uppercase tracking-[0.12em] text-[rgba(251,191,36,0.75)]">
          Sample Audit Preview
        </div>
        <p className="mt-1 text-[12px] text-zinc-500 dark:text-white/35">
          Live impersonation audit storage is not connected yet. These entries illustrate the
          expected event model only.
        </p>
      </div>
      <div className="text-[12px] text-zinc-400 dark:text-white/20 mb-4 font-mono">
        {MOCK_AUDIT.length} sample events
      </div>
      <div className="space-y-2">
        {MOCK_AUDIT.map((entry) => {
          const isStart = entry.action === 'SESSION_START'
          const isEnd = entry.action === 'SESSION_END'
          return (
            <div
              key={entry.id}
              className="flex items-start gap-3 px-4 py-3 rounded-lg border border-black/[0.07] dark:border-white/[0.05] bg-black/[0.02] dark:bg-white/[0.02]"
            >
              <div
                className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${isStart ? 'bg-[rgba(251,191,36,0.08)]' : isEnd ? 'bg-[rgba(52,211,153,0.08)]' : 'bg-[rgba(248,113,113,0.08)]'}`}
              >
                {isStart ? (
                  <Eye className="w-3 h-3 text-[rgba(251,191,36,0.6)]" />
                ) : isEnd ? (
                  <EyeOff className="w-3 h-3 text-[rgba(52,211,153,0.6)]" />
                ) : (
                  <AlertTriangle className="w-3 h-3 text-[rgba(248,113,113,0.6)]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-[12px] font-medium ${isStart ? 'text-[rgba(251,191,36,0.7)]' : isEnd ? 'text-[rgba(52,211,153,0.7)]' : 'text-[rgba(248,113,113,0.7)]'}`}
                  >
                    {entry.action.replace('_', ' ')}
                  </span>
                  <span className="text-[12px] text-zinc-600 dark:text-white/50">
                    {entry.tenant}
                  </span>
                  <span className="text-[11px] font-mono text-zinc-400 dark:text-white/20">
                    {entry.user_email}
                  </span>
                </div>
                {entry.reason && (
                  <div className="text-[11px] text-zinc-400 dark:text-white/25 italic mt-0.5">
                    {entry.reason}
                  </div>
                )}
              </div>
              <div className="flex-shrink-0 text-right">
                <div className="text-[10px] font-mono text-zinc-400 dark:text-white/20">
                  {entry.timestamp}
                </div>
                {entry.duration && (
                  <div className="text-[10px] font-mono text-zinc-400 dark:text-white/15 mt-0.5">
                    {entry.duration}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

export default function ParagonImpersonation() {
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<'tenants' | 'audit'>('tenants')
  const [confirmTarget, setConfirmTarget] = useState<TenantCard | null>(null)
  const [reason, setReason] = useState('')

  const {
    startImpersonation,
    isImpersonating,
    endImpersonation,
    impersonatedTenantName,
    impersonatedTenantId,
  } = useParagonStore()
  const navigate = useNavigate()

  /* ── Fetch tenant list via fleet health RPC ── */
  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['paragon-impersonation-tenants'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_fleet_health_matrix')
      if (error) throw error
      return (data ?? []) as unknown as TenantCard[]
    },
    staleTime: 60_000,
  })

  const filtered = useMemo(() => {
    if (!search.trim()) return tenants
    const q = search.toLowerCase()
    return tenants.filter(
      (t) => t.tenant_name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q)
    )
  }, [tenants, search])

  const handleImpersonate = async (tenant: TenantCard) => {
    try {
      const { data, error } = await supabase.functions.invoke('impersonate-tenant', {
        body: { tenant_id: tenant.tenant_id },
      })
      if (error) throw error
      startImpersonation(data.token, tenant.tenant_id, tenant.tenant_name)
      navigate(`/${tenant.slug}/admin`)
    } catch (err) {
      console.error('Impersonation failed:', err)
    }
  }

  const openConfirm = (tenant: TenantCard) => {
    setConfirmTarget(tenant)
    setReason('')
  }

  const closeConfirm = () => {
    setConfirmTarget(null)
    setReason('')
  }

  const confirmImpersonate = async () => {
    if (!confirmTarget || reason.length <= 5) return
    await handleImpersonate(confirmTarget)
    closeConfirm()
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden bg-zinc-50 dark:bg-[#171717] transition-colors duration-200">
      {confirmTarget && (
        <ConfirmModal
          target={confirmTarget}
          reason={reason}
          onReasonChange={setReason}
          onClose={closeConfirm}
          onConfirm={confirmImpersonate}
        />
      )}

      {/* Active session banner */}
      {isImpersonating() && (
        <div className="flex items-center justify-between px-6 py-2.5 bg-[rgba(248,113,113,0.06)] border-b border-[rgba(248,113,113,0.15)] danger-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-[rgba(248,113,113,0.7)]" />
            <span className="text-[12px] text-[rgba(248,113,113,0.7)]">
              Impersonating{' '}
              <span className="font-medium text-[rgba(248,113,113,0.9)]">
                {impersonatedTenantName}
              </span>
            </span>
          </div>
          <button
            onClick={() => endImpersonation()}
            className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-[11px] text-[rgba(248,113,113,0.7)] border border-[rgba(248,113,113,0.15)] hover:bg-[rgba(248,113,113,0.08)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35"
          >
            End Session <LogOut className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="h-14 flex items-center justify-between px-6 border-b border-black/[0.06] dark:border-white/[0.04] flex-shrink-0 bg-zinc-50 dark:bg-[#171717]">
        <div className="flex items-center gap-3">
          <span className="text-[14px] text-zinc-900 dark:text-zinc-50 font-medium">
            Impersonation
          </span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(248,113,113,0.08)] border border-[rgba(248,113,113,0.12)] text-[rgba(248,113,113,0.6)] font-mono tracking-wider">
            PRIVILEGED ACCESS
          </span>
        </div>
        <div className="flex items-center bg-black/[0.04] dark:bg-white/[0.03] border border-black/[0.08] dark:border-white/[0.06] rounded-lg p-0.5">
          {(['tenants', 'audit'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              aria-pressed={activeTab === tab}
              className={`text-[10px] font-mono uppercase px-2.5 py-1 rounded transition-[background-color,color,border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35 ${
                activeTab === tab
                  ? 'bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.2)] text-[#C9A84C]'
                  : 'text-zinc-500 dark:text-white/25 border border-transparent hover:text-zinc-700 dark:hover:text-white/40'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        {activeTab === 'tenants' && (
          <>
            <div className="p-4 rounded-xl bg-[rgba(251,191,36,0.04)] border border-[rgba(251,191,36,0.1)] mb-6 flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-[rgba(251,191,36,0.6)] flex-shrink-0 mt-0.5" />
              <div className="text-[12px] text-zinc-400 dark:text-white/35 leading-relaxed">
                Impersonation sessions are fully audited. All actions taken during an impersonation
                session are logged and attributed to your Paragon admin account. Sessions expire
                automatically after 60 minutes.
              </div>
            </div>

            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/[0.04] dark:bg-white/[0.03] border border-black/[0.08] dark:border-white/[0.06] mb-4 max-w-[400px]">
              <Search className="w-3.5 h-3.5 text-zinc-400 dark:text-white/20 flex-shrink-0" />
              <input
                aria-label="Search tenants"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tenants…"
                className="bg-transparent text-[13px] text-zinc-900 dark:text-zinc-50 placeholder-zinc-400/60 dark:placeholder-white/15 outline-none focus-visible:ring-2 focus-visible:ring-[rgba(248,113,113,0.3)] rounded flex-1"
              />
            </div>

            {isLoading ? (
              <div className="text-center text-zinc-400 dark:text-white/20 py-12 text-[13px] font-mono">
                Loading tenants…
              </div>
            ) : (
              <div className="space-y-1">
                {filtered.map((tenant) => {
                  const isActive = isImpersonating() && impersonatedTenantId === tenant.tenant_id
                  const isSuspended =
                    tenant.status === 'suspended' || tenant.health_status === 'critical'
                  return (
                    <div
                      key={tenant.tenant_id}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-[background-color,color,border-color] ${
                        isActive
                          ? 'border-[rgba(248,113,113,0.2)] bg-[rgba(248,113,113,0.03)]'
                          : isSuspended
                            ? 'border-black/[0.05] dark:border-white/[0.04] bg-transparent opacity-50'
                            : 'border-black/[0.07] dark:border-white/[0.05] bg-black/[0.02] dark:bg-white/[0.02] hover:bg-black/[0.04] dark:hover:bg-white/[0.03]'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-black/[0.04] dark:bg-white/[0.04] border border-black/[0.06] dark:border-white/[0.05] flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-4 h-4 text-zinc-400 dark:text-white/25" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13px] text-zinc-900 dark:text-zinc-50 truncate">
                            {tenant.tenant_name}
                          </span>
                          <PlanBadge plan={tenant.plan} />
                          {isSuspended && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded border border-[rgba(248,113,113,0.15)] text-[rgba(248,113,113,0.5)] font-mono">
                              suspended
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] font-mono text-zinc-400 dark:text-white/20 flex items-center gap-2 mt-0.5">
                          <span>{tenant.slug}</span>
                          <span className="text-zinc-300 dark:text-white/10">·</span>
                          <span>{tenant.owner_email ?? '—'}</span>
                          <span className="text-zinc-300 dark:text-white/10">·</span>
                          <span>{tenant.slug}.phyre.tech</span>
                        </div>
                      </div>
                      {isActive ? (
                        <button
                          onClick={() => endImpersonation()}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] bg-[rgba(248,113,113,0.1)] border border-[rgba(248,113,113,0.15)] text-[rgba(248,113,113,0.7)] hover:bg-[rgba(248,113,113,0.15)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35"
                        >
                          <EyeOff className="w-3 h-3" /> End
                        </button>
                      ) : (
                        <button
                          onClick={() => openConfirm(tenant)}
                          disabled={isSuspended}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] bg-black/[0.04] dark:bg-white/[0.04] border border-black/[0.07] dark:border-white/[0.06] text-zinc-500 dark:text-white/35 hover:bg-black/[0.07] dark:hover:bg-white/[0.07] transition-colors disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35"
                        >
                          <LogIn className="w-3 h-3" /> Impersonate
                        </button>
                      )}
                    </div>
                  )
                })}
                {filtered.length === 0 && !isLoading && (
                  <div className="text-center text-zinc-400 dark:text-white/15 py-12 text-[13px]">
                    No tenants match search
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {activeTab === 'audit' && <AuditTab />}
      </div>
    </div>
  )
}
